import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { DahliaMark } from '@/components/BrandMark';
import { ShoppingBag, ArrowLeft, Package, Check, Clock } from 'lucide-react';

const fmt = (n, cur = 'NZD') => `$${Number(n || 0).toFixed(2)} ${cur}`;

const STATUS_STYLES = {
  pending: { icon: Clock, label: 'Awaiting payment', bg: 'rgba(75,24,56,0.08)' },
  paid: { icon: Check, label: 'Paid — in production', bg: 'rgba(91,110,44,0.12)' },
  shipped: { icon: Package, label: 'Shipped', bg: 'rgba(75,24,56,0.15)' },
  cancelled: { icon: Clock, label: 'Cancelled', bg: 'rgba(154,44,44,0.12)' },
};

export default function FounderMyOrders() {
  const { orderId } = useParams();
  if (orderId) return <OrderDetail id={orderId} />;
  return <OrdersList />;
}

function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/founders/orders');
        setOrders(data.orders || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-5" data-testid="founder-my-orders">
      <div className="h-panel-thin p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="h-eyebrow">My Heritage Orders</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Your bundle add-ons</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Every add-on you&rsquo;ve placed. Invoices and payment references live here.</p>
        </div>
        <Link to="/console/founder/store" className="h-btn text-xs" data-testid="my-orders-shop-link">
          <ShoppingBag size={14} className="mr-2" /> Add more to my order
        </Link>
      </div>

      {loading && <div className="h-eyebrow">Loading...</div>}

      {!loading && orders.length === 0 && (
        <Panel className="p-8 text-center">
          <DahliaMark size={40} />
          <div className="h-serif text-2xl mt-3" style={{ color: 'var(--heritage-plum)' }}>No add-on orders yet</div>
          <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Browse the Founder Store to add items to your bundle.</div>
          <Link to="/console/founder/store" className="h-btn inline-flex mt-4">Open Founder Store</Link>
        </Panel>
      )}

      <div className="grid gap-4">
        {orders.map((o) => {
          const st = STATUS_STYLES[o.status] || STATUS_STYLES.pending;
          const Icon = st.icon;
          return (
            <Panel key={o.id} className="p-5" data-testid={`my-order-${o.invoice_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="h-eyebrow text-[10px]">{new Date(o.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  <div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{o.items.length} item{o.items.length === 1 ? '' : 's'} · {fmt(o.total, o.currency)}</div>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs h-serif" style={{ background: st.bg, color: 'var(--heritage-plum)' }}>
                  <Icon size={12} /> {st.label}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {o.items.slice(0, 4).map((li) => (
                  <div key={li.slug} className="w-14 h-14 rounded-md overflow-hidden bg-[rgba(75,24,56,0.05)]">
                    {li.image_url ? <img src={li.image_url} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                ))}
                {o.items.length > 4 && <div className="w-14 h-14 rounded-md flex items-center justify-center h-serif text-sm" style={{ background: 'rgba(75,24,56,0.08)', color: 'var(--heritage-plum)' }}>+{o.items.length - 4}</div>}
              </div>
              <div className="mt-3">
                <Link to={`/console/founder/orders/${o.id}`} className="h-btn-outline text-xs" data-testid={`my-order-view-${o.invoice_number}`}>View invoice</Link>
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

function OrderDetail({ id }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingCard, setPayingCard] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/founders/orders/${id}`);
        setOrder(data.order);
      } finally { setLoading(false); }
    })();
  }, [id]);

  const payWithStripe = async () => {
    setPayingCard(true);
    try {
      const { data } = await api.post('/checkout/founder-order', { order_id: id, origin_url: window.location.origin });
      if (data?.url) { window.location.href = data.url; }
    } catch (e) {
      setPayingCard(false);
      alert(e?.response?.data?.detail || 'Could not start card checkout');
    }
  };

  if (loading) return <div className="h-eyebrow">Loading...</div>;
  if (!order) return (
    <div className="text-center">
      <div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>Order not found</div>
      <Link to="/console/founder/orders" className="h-btn-outline mt-4 inline-flex text-xs"><ArrowLeft size={14} className="mr-2" /> Back to my orders</Link>
    </div>
  );

  const st = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
  const Icon = st.icon;

  return (
    <div className="space-y-4" data-testid="my-order-detail">
      <button onClick={() => navigate(-1)} className="h-btn-outline text-xs" data-testid="order-back"><ArrowLeft size={14} className="mr-2" /> Back</button>

      <Panel className="p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="h-eyebrow">Invoice</div>
            <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{order.invoice_number}</div>
            <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>Placed {new Date(order.created_at).toLocaleString('en-NZ')}</div>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs h-serif" style={{ background: st.bg, color: 'var(--heritage-plum)' }}>
            <Icon size={12} /> {st.label}
          </div>
        </div>

        <div className="mt-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(75,24,56,0.2)' }}>
          <table className="w-full text-sm">
            <thead style={{ background: 'rgba(75,24,56,0.06)' }}>
              <tr>
                <th className="text-left p-3 h-eyebrow text-[10px]">Item</th>
                <th className="text-center p-3 h-eyebrow text-[10px]">Qty</th>
                <th className="text-right p-3 h-eyebrow text-[10px]">Unit</th>
                <th className="text-right p-3 h-eyebrow text-[10px]">Line total</th>
              </tr>
            </thead>
            <tbody style={{ color: 'var(--heritage-plum)' }}>
              {order.items.map((l) => (
                <tr key={l.slug} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.1)' }}>
                  <td className="p-3"><div className="h-serif">{l.name}</div><div className="text-[11px] opacity-70">{l.colourway_swatch}</div></td>
                  <td className="p-3 text-center h-serif">{l.qty}</td>
                  <td className="p-3 text-right h-serif">${Number(l.unit_price).toFixed(2)}</td>
                  <td className="p-3 text-right h-serif">${Number(l.line_total).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(75,24,56,0.04)' }}>
                <td colSpan={3} className="p-3 h-eyebrow text-right">Total</td>
                <td className="p-3 text-right h-serif">${Number(order.total).toFixed(2)} {order.currency}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl p-4" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
            <div className="h-eyebrow">Bank transfer</div>
            <div className="mt-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <div><span className="opacity-70">Account name:</span> <span className="h-serif">{order.bank_name}</span></div>
              <div><span className="opacity-70">Account number:</span> <span className="h-serif">{order.bank_account}</span></div>
              <div><span className="opacity-70">Reference:</span> <span className="h-serif">{order.invoice_number}</span></div>
            </div>
            {order.status === 'pending' && (
              <>
                <div className="text-xs opacity-70 mt-2" style={{ color: 'var(--heritage-plum)' }}>Once your payment lands we&rsquo;ll email you and update this status.</div>
                <button onClick={payWithStripe} disabled={payingCard}
                  className="h-btn-outline text-xs mt-3"
                  style={{ borderColor: '#635BFF', color: '#635BFF' }} data-testid="order-pay-card">
                  {payingCard ? 'Redirecting…' : 'Pay now with card (Stripe)'}
                </button>
                <div className="text-[10px] opacity-60 mt-1" style={{ color: 'var(--heritage-plum)' }}>Bank transfer has no fees. Card adds ~3% Stripe processing fee.</div>
              </>
            )}
          </div>
          <div className="rounded-xl p-4" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
            <div className="h-eyebrow">Delivery</div>
            <div className="mt-2 text-sm whitespace-pre-line" style={{ color: 'var(--heritage-plum)' }}>{order.delivery_address || 'Ships with your Founder Bundle.'}</div>
            {order.delivery_notes && <div className="mt-2 text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>Notes: {order.delivery_notes}</div>}
            {order.status === 'shipped' && (
              <div className="mt-3 text-xs" style={{ color: 'var(--heritage-plum)' }}>
                <div className="h-eyebrow text-[10px]">Tracking</div>
                <div className="h-serif">{order.shipping_courier} {order.shipping_tracking}</div>
              </div>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}
